import { cacheGet, cacheSet,cacheDel } from './cacheUtil';

// Cache keys
const CACHE_KEYS = {
  SITE_CONFIG: 'site_config',
  BASIC_HOME_CONTENT: 'basic_home_content',
  FEATURED_GROUPS: 'featured_groups',
  LATEST_PROGRAMS: 'latest_programs',
  YOUTUBE_VIDEOS: 'youtube_videos',
  LATEST_ARTICLES: 'latest_articles',
  LATEST_NEWS: 'latest_news'
};

// TTL in seconds (6 hours = 21600 seconds)
const CACHE_TTL = 21600;

/**
 * Cached version of fetchSiteConfig
 */
export async function getCachedSiteConfig() {
  // Try to get from cache first
  const cachedData = await cacheGet(CACHE_KEYS.SITE_CONFIG);
  if (cachedData) {
    return cachedData;
  }

  // If not in cache, import the original function and call it
  const { fetchSiteConfig } = await import('../services/SiteConfigService');
  const data = await fetchSiteConfig();
  
  // Store in cache
  await cacheSet(CACHE_KEYS.SITE_CONFIG, data, CACHE_TTL);
  
  return data;
}

/**
 * Cached version of fetchBasicHomeContent
 */
export async function getCachedBasicHomeContent() {
  // Try to get from cache first
  const cachedData = await cacheGet(CACHE_KEYS.BASIC_HOME_CONTENT);
  if (cachedData) {
    return cachedData;
  }

  // If not in cache, import the original function and call it
  const { fetchBasicHomeContent } = await import('../services/SiteConfigService');
  const data = await fetchBasicHomeContent();
  
  // Store in cache
  await cacheSet(CACHE_KEYS.BASIC_HOME_CONTENT, data, CACHE_TTL);
  
  return data;
}

/**
 * Cached version of fetchFeaturedGroups
 */
export async function getCachedFeaturedGroups(page: number = 1, limit: number = 6) {
  const cacheKey = `${CACHE_KEYS.FEATURED_GROUPS}_${page}_${limit}`;
  
  // Try to get from cache first
  const cachedData = await cacheGet(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  // If not in cache, import the original function and call it
  const { fetchFeaturedGroups } = await import('../services/GroupService');
  const data = await fetchFeaturedGroups(page, limit);
  
  // Store in cache
  await cacheSet(cacheKey, data, CACHE_TTL);
  
  return data;
}

/**
 * Cached version of fetchProgramsByPagination
 */
export async function getCachedLatestPrograms(page: number = 1, limit: number = 6, orderBy: string = 'createdAt') {
  const cacheKey = `${CACHE_KEYS.LATEST_PROGRAMS}_${page}_${limit}_${orderBy}`;
  
  // Try to get from cache first
  const cachedData = await cacheGet(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  // If not in cache, import the original function and call it
  const { fetchProgramsByPagination } = await import('../services/ProgramService');
  const data = await fetchProgramsByPagination(page, limit, orderBy);
  
  // Store in cache
  await cacheSet(cacheKey, data, CACHE_TTL);
  
  return data;
}

/**
 * Cached version of YouTube videos fetch for BasicHomeYoutubeSection
 */
export async function getCachedYoutubeVideos(limit: number = 4, isSpecial: boolean = false) {
  const cacheKey = `${CACHE_KEYS.YOUTUBE_VIDEOS}_${limit}_${isSpecial}`;
  
  // Try to get from cache first
  const cachedData = await cacheGet(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  // If not in cache, fetch directly (as the component does)
  const { API_BASE } = await import('../Config');
  const queryParams = new URLSearchParams({
    limit: limit.toString(),
    isActive: "true",
    isSpecial: isSpecial.toString()
  });
  
  const response = await fetch(`${API_BASE}/youtube-videos?${queryParams}`);
  if (!response.ok) {
    throw new Error("Failed to load videos");
  }
  
  const data = await response.json();
  
  // Store in cache
  await cacheSet(cacheKey, data, CACHE_TTL);
  
  return data;
}

/**
 * Cached version of fetchLatestContentByType for articles
 */
export async function getCachedLatestArticles(limit: number = 3) {
  const cacheKey = `${CACHE_KEYS.LATEST_ARTICLES}_${limit}`;
  
  // Try to get from cache first
  const cachedData = await cacheGet(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  // If not in cache, import the original function and call it
  const { fetchLatestContentByType } = await import('../services/ContentService');
  const data = await fetchLatestContentByType('article', limit);
  
  // Store in cache
  await cacheSet(cacheKey, data, CACHE_TTL);
  
  return data;
}

/**
 * Cached version of fetchLatestContentByType for news
 */
export async function getCachedLatestNews(limit: number = 3) {
  const cacheKey = `${CACHE_KEYS.LATEST_NEWS}_${limit}`;
  
  // Try to get from cache first
  const cachedData = await cacheGet(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  // If not in cache, import the original function and call it
  const { fetchLatestContentByType } = await import('../services/ContentService');
  const data = await fetchLatestContentByType('news', limit);
  
  // Store in cache
  await cacheSet(cacheKey, data, CACHE_TTL);
  
  return data;
}

/**
 * Clear all home page related caches
 */
export async function clearHomePageCaches() {
  const cacheKeys = Object.values(CACHE_KEYS);
  for (const key of cacheKeys) {
    await cacheDel(key);
  }
}
